import {
  Box,
  Grid2,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useState, useEffect } from "react";
import { Button, SelectBox, DateRangePicker } from "../../common";
import { DATE_RANGE_OPTIONS } from "../../../constants";
import moment, { Moment } from "moment";
import { getDateRange } from "../../../utils/dateUtil";
import { DateRange } from "../../../types";
import { useAtom } from "jotai";
import { invoiceFilterStateAtom, userAtom } from "../../../atoms";
import { useFetchOrganizationsQuery } from "../../../api";
import { OrganizationModel } from "../../../models";
import { DropdownItem } from "../../../types";
import { Role } from "../../../enums";

const dateRanges = [
  { key: "Select", value: "select" },
  ...DATE_RANGE_OPTIONS.map((option) => ({
    key: option,
    value: option.replace(/\s+/g, "").replace(/^\w/, (c) => c.toLowerCase()),
  })),
];
type KPIHeaderProps = {
  fromDate: string;
  toDate: string;
  setFromDate: (date: string) => void;
  setToDate: (date: string) => void;
  selectedOrgId?: number;
  setSelectedOrgId: (id: number) => void;
  setSelectedOrgName: (name: string) => void;
  onDownload: () => void;
};
export const KPIHeader = ({
  fromDate,
  toDate,
  setFromDate,
  setToDate,
  selectedOrgId,
  setSelectedOrgId,
  setSelectedOrgName,
  onDownload,
}: KPIHeaderProps) => {
  const [selectedWeek, setSelectedWeek] = useState("thisWeek");

  const [user] = useAtom(userAtom);
  const [filterValues, setFilterValues] = useAtom(invoiceFilterStateAtom);

  const [organizationsDropdown, setOrganizationsDropdown] = useState<
    DropdownItem[]
  >([]);

  const fetchOrganizationsQuery = useFetchOrganizationsQuery({
    enabled:
      user?.roleCode === Role.SuperAdmin || user?.roleCode === Role.Admin,
  });
  useEffect(() => {
    if (fetchOrganizationsQuery.isSuccess) {
      const dropdown = [
        {
          key: "Select",
          value: 0,
        },
        ...OrganizationModel.buildOrganizationDropdown(
          fetchOrganizationsQuery.data,
        ),
      ];

      setOrganizationsDropdown(dropdown);
    }
  }, [fetchOrganizationsQuery.isSuccess]);
  useEffect(() => {
    if (organizationsDropdown.length > 0 && selectedOrgId) {
      const selectedOrganization = organizationsDropdown.find(
        (org) => Number(org.value) === selectedOrgId,
      );

      setSelectedOrgName(selectedOrganization?.key || "");
    }
  }, [organizationsDropdown, selectedOrgId]);

  const handleDateChange = (selectedDateRange: string) => {
    setSelectedWeek(selectedDateRange);

    if (selectedDateRange !== "customDate") {
      const dateRange: DateRange = getDateRange(selectedDateRange);

      setFromDate(dateRange.startDate.toString());
      setToDate(dateRange.endDate.toString());
    }
  };
  const handleCustomDateChange = (
    startDate: Moment | null,
    endDate: Moment | null,
  ) => {
    if (!startDate) {
      startDate = moment(fromDate);
    }

    if (!endDate) {
      endDate = moment(toDate);
    }

    if (startDate && endDate) {
      const startDateStartOfTheDay = startDate.startOf("day").toISOString();

      const endDateEndOfTheDay = endDate.endOf("day").toISOString();

      setFromDate(startDateStartOfTheDay);
      setToDate(endDateEndOfTheDay);
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: {
          xs: "column",
          sm: "row",
        },
        gap: 2,
      }}
    >
      <Grid2 container spacing={2} alignItems="flex-end" flexDirection="row">
        <Grid2 size={{ xs: 12, sm: "auto" }}>
          <SelectBox
            label="Date"
            name="date"
            value={selectedWeek}
            onChange={(e) => handleDateChange(e.target.value)}
            dropdown={dateRanges}
            sx={{
              width: {
                xs: "100%",
                sm: "250px",
              },
              height: "33px",
            }}
          />
        </Grid2>

        {user?.roleCode === Role.SuperAdmin && (
          <Grid2 size={{ xs: 12, sm: "auto" }}>
            <FormControl
              size="small"
              sx={{
                width: {
                  xs: "100%",
                  sm: "250px",
                },
              }}
            >
              <InputLabel>Organization</InputLabel>

              <Select
                sx={{
                  height: "33px",
                }}
                value={selectedOrgId ?? 0}
                label="Organization"
                onChange={(e) => {
                  const value = e.target.value;

                  if (Number(value) === 0) {
                    setSelectedOrgId(0);
                    setSelectedOrgName("");
                    return;
                  }

                  const orgId = Number(value);

                  const selectedOrganization = organizationsDropdown.find(
                    (org) => Number(org.value) === orgId,
                  );

                  setSelectedOrgId(orgId);
                  setSelectedOrgName(selectedOrganization?.key || "");
                }}
              >
                {organizationsDropdown.map((org, index) => (
                  <MenuItem key={index} value={org.value}>
                    {org.key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
        )}

        {selectedWeek === "customDate" && (
          <Grid2 size={{ xs: 12, sm: "auto" }}>
            <DateRangePicker
              start={moment(fromDate)}
              end={moment(toDate)}
              onDateChange={(startDate, endDate) => {
                handleCustomDateChange(startDate, endDate);
              }}
            />
          </Grid2>
        )}
      </Grid2>

      <Button
        label="Download"
        variantType="secondary"
        icon={FileDownloadOutlinedIcon}
        onClick={onDownload}
        sx={{
          marginTop: "18px",
          height: "30px",
          minWidth: "140px",
          width: {
            xs: "100%",
            sm: "140px",
          },
          padding: {
            xl: "1rem",
            lg: "0",
            xs: "0",
            sm: "0",
          },
        }}
      />
    </Box>
  );
};
